// src/pages/HomePage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useSettings } from '../lib/SettingsContext';
import { listenToEvents, getTown } from '../lib/db';
import { EventCardHero, EventCardSmall } from '../components/events/EventCard';
import { SponsorStrip, PromotedBadge } from '../components/shared/SponsorStrip';
import BottomNav from '../components/layout/BottomNav';
import InstallPrompt from '../components/shared/InstallPrompt';
import { HeaderAvatar } from '../components/shared/HeaderAvatar';
import { ReferBanner } from '../components/shared/ReferBanner';
import { isToday, isTomorrow, addDays, endOfWeek, startOfMonth, endOfMonth, addMonths } from 'date-fns';

const HomePage = () => {
  const navigate = useNavigate();
  const { user, profile, isOrganiser } = useAuth();
  const { t } = useSettings();

  const [events, setEvents] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [category, setCategory] = useState(null);
  const [timeFilter, setTimeFilter] = useState('all'); // all | today | weekend
  const [town, setTown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOrganiserNudge, setShowOrganiserNudge] = useState(false);

  const townId = profile?.defaultTownId || localStorage.getItem('gather-town-id');

  const CATEGORIES = [
    { label: t('allEvents'), value: null },
    { label: 'Sport GAA 🏐', value: 'sport_gaa' },
    { label: 'Sport 🏃', value: 'sport' },
    { label: 'Music 🎵', value: 'music' },
    { label: 'Kids 🧒', value: 'kids' },
    { label: 'Community 🤝', value: 'community' },
    { label: 'Markets 🛒', value: 'markets' },
    { label: 'Arts 🎭', value: 'arts' },
    { label: 'Health 🏥', value: 'health' },
    { label: 'Farming 🌾', value: 'farming' },
    { label: 'Food 🍽', value: 'food' },
    { label: 'Education 📚', value: 'education' },
    { label: 'Social 🎉', value: 'social' },
  ];

  // Show organiser nudge on 3rd visit
  useEffect(() => {
    const visits = parseInt(localStorage.getItem('gather-visit-count') || '0') + 1;
    localStorage.setItem('gather-visit-count', String(visits));
    const dismissed = localStorage.getItem('gather-organiser-nudge-dismissed');
    if (visits >= 3 && !dismissed) {
      setTimeout(() => setShowOrganiserNudge(true), 3000);
    }
  }, []);

  useEffect(() => {
    if (!townId) { navigate('/welcome'); return; }
    getTown(townId).then(setTown);
    const unsub = listenToEvents(townId, (evs) => {
      setEvents(evs);
      setLoading(false);
    });
    return unsub;
  }, [townId, navigate]);

  // Apply filters
  useEffect(() => {
    let result = [...events];

    // Category filter
    if (category) result = result.filter(e => e.category === category);

    // Time filter
    if (timeFilter === 'today') {
      result = result.filter(e => {
        const d = e.nextDate?.toDate ? e.nextDate.toDate() : new Date(e.nextDate);
        return isToday(d);
      });
    } else if (timeFilter === 'tomorrow') {
      result = result.filter(e => {
        const d = e.nextDate?.toDate ? e.nextDate.toDate() : new Date(e.nextDate);
        return isTomorrow(d);
      });
    } else if (timeFilter === 'weekend') {
      const friday = addDays(new Date(), (5 - new Date().getDay() + 7) % 7);
      const sunday = addDays(friday, 2);
      result = result.filter(e => {
        const d = e.nextDate?.toDate ? e.nextDate.toDate() : new Date(e.nextDate);
        return d >= friday && d <= sunday;
      });
    } else if (timeFilter === 'thisweek') {
      // From now through the end of the current Mon–Sun week
      const end = endOfWeek(new Date(), { weekStartsOn: 1 });
      result = result.filter(e => {
        const d = e.nextDate?.toDate ? e.nextDate.toDate() : new Date(e.nextDate);
        return d <= end;
      });
    } else if (timeFilter === 'thismonth') {
      const end = endOfMonth(new Date());
      result = result.filter(e => {
        const d = e.nextDate?.toDate ? e.nextDate.toDate() : new Date(e.nextDate);
        return d <= end;
      });
    } else if (timeFilter === 'nextmonth') {
      const start = startOfMonth(addMonths(new Date(), 1));
      const end = endOfMonth(start);
      result = result.filter(e => {
        const d = e.nextDate?.toDate ? e.nextDate.toDate() : new Date(e.nextDate);
        return d >= start && d <= end;
      });
    }

    setFiltered(result);
  }, [events, category, timeFilter]);

  // Promoted events shown first, then regular
  const promoted = filtered.filter(e => e.promoted || e.source === 'promoted');
  const regular = filtered.filter(e => !e.promoted && e.source !== 'promoted');
  const [featured, ...rest] = regular;

  // Stats bar count — always "this week" regardless of active filter,
  // so the number stays a stable reference point as people flip pills
  const thisWeekCount = events.filter(e => {
    const d = e.nextDate?.toDate ? e.nextDate.toDate() : new Date(e.nextDate);
    return d <= endOfWeek(new Date(), { weekStartsOn: 1 });
  }).length;

  return (
    <div className="page" style={{ background: 'var(--cream)' }}>
      {/* Town colour banner — personalised per town, up to 3 colours
          (Irish counties usually have 2, sometimes 3). Falls back to a
          single sage strip if the town has no colours set. */}
      {town?.brandColors?.length > 0 ? (
        <div style={{ display: 'flex', height: 18, width: '100%', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
          {town.brandColors.map((c, i) => (
            <div key={i} style={{ flex: 1, background: c }} />
          ))}
        </div>
      ) : (
        <div style={{ height: 18, width: '100%', background: 'var(--sage-deep)', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }} />
      )}

      {/* Skip to content — screen reader */}
      <a href="#main-content" className="skip-link">Skip to events</a>

      {/* Header */}
      <header style={s.header} role="banner">
        <div style={s.topRow}>
          <div>
            <button
              style={s.locationBtn}
              onClick={() => navigate('/explore')}
              aria-label={`Current location: ${town?.name || 'Your town'}, ${town?.county || ''}. Tap to change.`}
            >
              📍 <span style={{ color: 'var(--ink)', fontWeight: 700 }}>{town?.name || 'Your town'}</span>
              {town?.county && `, ${town.county}`}
              <span style={s.changeLink}> {t('changeLocation')}</span>
            </button>
            <h1 style={s.h1}>What's on in {town?.name || 'your town'}</h1>
            <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 500, marginTop: 3 }}>
              {town?.tagline || 'Everything happening locally, in one place'}
            </div>
          </div>
          <HeaderAvatar />
        </div>

        {/* This Week stats bar — computed from loaded events, no extra call */}
        {!loading && events.length > 0 && (
          <div style={s.statsBar}>
            <span style={s.statsBarText}>
              📅 <strong>{thisWeekCount}</strong> on this week
            </span>
            <span style={s.statsBarDivider}>·</span>
            <span style={s.statsBarText}>
              ✨ <strong>{events.length}</strong> upcoming in total
            </span>
          </div>
        )}

        {/* Multi-sponsor strip */}
        <SponsorStrip townId={townId} page="events" />
      </header>

      {/* Time filter pills */}
      <div style={s.timeFilter} role="group" aria-label="Time filter">
        {[
          { label: 'All dates', value: 'all' },
          { label: t('today'), value: 'today' },
          { label: 'Tomorrow', value: 'tomorrow' },
          { label: t('weekend'), value: 'weekend' },
          { label: 'This week', value: 'thisweek' },
          { label: 'This month', value: 'thismonth' },
          { label: 'Next month', value: 'nextmonth' },
        ].map(tf => (
          <button
            key={tf.value}
            style={{
              ...s.timePill,
              background: timeFilter === tf.value ? 'var(--sage-dark)' : 'var(--white)',
              color: timeFilter === tf.value ? 'white' : 'var(--ink)',
              border: timeFilter === tf.value ? 'none' : '1.5px solid var(--border)',
            }}
            onClick={() => setTimeFilter(tf.value)}
            aria-pressed={timeFilter === tf.value}
          >
            {tf.label}
          </button>
        ))}
      </div>

      {/* Category filters */}
      <nav style={s.catScroll} aria-label="Filter by category" role="navigation">
        {CATEGORIES.map(cat => (
          <button
            key={cat.label}
            style={{
              ...s.catPill,
              background: category === cat.value ? 'var(--sage-dark)' : 'var(--white)',
              color: category === cat.value ? 'white' : 'var(--ink)',
              border: category === cat.value ? 'none' : '1.5px solid var(--border)',
            }}
            onClick={() => setCategory(cat.value)}
            aria-pressed={category === cat.value}
            aria-label={`Filter by ${cat.label}`}
          >
            {cat.label}
          </button>
        ))}
      </nav>

      {/* Feed */}
      <main id="main-content" style={s.feed} aria-live="polite" aria-label="Events feed">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }} role="status" aria-label={t('loading')}>
            <div className="spinner" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state" role="status">
            <div className="empty-state-icon" aria-hidden="true">📅</div>
            <div className="empty-state-title">{t('noEvents')}</div>
            <div className="empty-state-body">
              {timeFilter !== 'all'
                ? `Nothing on ${
                    timeFilter === 'today' ? 'today' :
                    timeFilter === 'tomorrow' ? 'tomorrow' :
                    timeFilter === 'weekend' ? 'this weekend' :
                    timeFilter === 'thisweek' ? 'this week' :
                    timeFilter === 'thismonth' ? 'this month' :
                    timeFilter === 'nextmonth' ? 'next month' : 'for this filter'
                  }. Try "All dates" to see everything.`
                : t('noEventsBody')}
            </div>
            {timeFilter !== 'all' && (
              <button
                style={{ marginTop: 16, background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none', borderRadius: 100, padding: '10px 24px', fontSize: 14, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}
                onClick={() => setTimeFilter('all')}
              >
                Show all events
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Promoted events */}
            {promoted.length > 0 && (
              <>
                <div className="section-label" aria-label="Promoted events section">
                  {t('promoted')}
                </div>
                {promoted.map(event => (
                  <div key={event.id} style={{ position: 'relative' }}>
                    <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 10 }}>
                      <PromotedBadge type="promoted" />
                    </div>
                    <EventCardSmall event={event} />
                  </div>
                ))}
              </>
            )}

            {/* Featured hero */}
            {featured && (
              <>
                <div className="section-label">{t('thisWeekend')}</div>
                <EventCardHero event={featured} />
                {rest.length > 0 && (
                  <div className="section-label" style={{ marginTop: 8 }}>
                    {t('comingUp')}
                  </div>
                )}
              </>
            )}

            {/* Regular events */}
            {rest.map(event => (
              <EventCardSmall key={event.id} event={event} />
            ))}
          </>
        )}
      </main>

      {/* FAB for organisers */}
      {isOrganiser && (
        <button
          style={s.fab}
          onClick={() => navigate('/post')}
          aria-label="Post a new event"
        >
          <span aria-hidden="true">＋</span>
        </button>
      )}


      {/* Public footer links */}
      <div style={{ padding: '8px 20px 16px', display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '4px 16px' }}>
        {[
          ['About', '/about'],
          ['Privacy', '/privacy'],
          ['FAQ', '/faq'],
          ['Contact', '/contact'],
        ].map(([label, path]) => (
          <button key={path} onClick={() => navigate(path)}
            style={{ background: 'none', border: 'none', fontSize: 11, color: 'var(--muted)', fontWeight: 600, cursor: 'pointer', fontFamily: 'Nunito, sans-serif', padding: '4px 0' }}>
            {label}
          </button>
        ))}
      </div>

      {/* Know an organiser? nudge */}
      {showOrganiserNudge && (
        <div style={{ position: 'fixed', bottom: 96, left: 12, right: 12, maxWidth: 406, margin: '0 auto', background: 'var(--ink)', borderRadius: 18, padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 12, zIndex: 200, boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
          <span style={{ fontSize: 26, flexShrink: 0 }}>📣</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'white', marginBottom: 3 }}>Know a local organiser?</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>GAA, drama, yoga, markets — let them know they can post for free on Gather.</div>
            <button onClick={() => {
              const msg = `Hey! There's a free community noticeboard for ${town?.name || 'our town'} called Gather — local clubs and organisers can post their events for free. Might be worth putting yours up: https://gatheryourcommunity.com`;
              window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
              localStorage.setItem('gather-organiser-nudge-dismissed', 'true');
              setShowOrganiserNudge(false);
            }} style={{ marginTop: 8, background: '#25D366', color: 'white', border: 'none', borderRadius: 10, padding: '7px 14px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
              Send via WhatsApp
            </button>
          </div>
          <button onClick={() => { localStorage.setItem('gather-organiser-nudge-dismissed', 'true'); setShowOrganiserNudge(false); }}
            aria-label="Dismiss"
            style={{ background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)', border: 'none', borderRadius: 8, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
            ✕
          </button>
        </div>
      )}

      <InstallPrompt />
      <ReferBanner />
      <BottomNav />
    </div>
  );
};

const s = {
  header: { padding: '52px 20px 0' },
  statsBar: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 12px', marginTop: 10,
    background: 'var(--sage-pale)', borderRadius: 12,
  },
  statsBarText: { fontSize: 12.5, color: 'var(--sage-dark)', fontWeight: 500 },
  statsBarDivider: { color: 'var(--sage-dark)', opacity: 0.4 },
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  locationBtn: {
    display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap',
    fontSize: 12, color: 'var(--muted)', fontWeight: 600,
    letterSpacing: 0.3, textTransform: 'uppercase', marginBottom: 3,
    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
    fontFamily: 'Nunito, sans-serif', textAlign: 'left', minHeight: 'auto',
  },
  changeLink: { color: 'var(--sage-deep)', fontWeight: 700, textDecoration: 'underline' },
  h1: { fontSize: 30, fontWeight: 800, color: 'var(--ink)', letterSpacing: '-0.5px', margin: 0 },
  avatar: {
    width: 40, height: 40, background: 'var(--sage)', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'var(--sage-dark)', fontSize: 15, fontWeight: 800,
    cursor: 'pointer', flexShrink: 0, border: '2px solid rgba(255,255,255,0.6)',
    minHeight: 40, minWidth: 40,
  },
  timeFilter: {
    display: 'flex', gap: 8, padding: '10px 20px 4px', overflowX: 'auto', scrollbarWidth: 'none',
  },
  timePill: {
    flexShrink: 0, padding: '8px 16px', borderRadius: 100,
    fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
    transition: 'all 0.15s', fontFamily: 'Nunito, sans-serif', minHeight: 36,
  },
  catScroll: {
    display: 'flex', gap: 8, overflowX: 'auto',
    padding: '6px 20px 10px', scrollbarWidth: 'none',
  },
  catPill: {
    flexShrink: 0, padding: '8px 16px', borderRadius: 100,
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
    whiteSpace: 'nowrap', transition: 'all 0.15s',
    fontFamily: 'Nunito, sans-serif', minHeight: 36,
  },
  feed: { padding: '4px 18px 20px', display: 'flex', flexDirection: 'column', gap: 10 },
  fab: {
    position: 'fixed', bottom: 96, right: 18,
    width: 52, height: 52, background: 'var(--sage-deep)',
    borderRadius: 16, border: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 26, color: 'white',
    cursor: 'pointer', boxShadow: '0 6px 18px rgba(122,154,124,0.45)',
    zIndex: 99,
  },
};

export default HomePage;
